import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { User, Phone, Mail, Instagram, AtSign } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/i18n/LanguageContext';
import { recipientSchema } from '@/lib/validations';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

interface AddRecipientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const contactTypes = [
  { value: 'phone', label: { en: 'Phone', es: 'Teléfono' }, icon: Phone },
  { value: 'email', label: { en: 'Email', es: 'Correo' }, icon: Mail },
  { value: 'instagram', label: { en: 'Instagram', es: 'Instagram' }, icon: Instagram },
  { value: 'facebook', label: { en: 'Facebook', es: 'Facebook' }, icon: AtSign },
  { value: 'twitter', label: { en: 'X/Twitter', es: 'X/Twitter' }, icon: AtSign },
];

const relationships = [
  { value: 'parent', label: { en: 'Parent', es: 'Padre/Madre' } },
  { value: 'grandparent', label: { en: 'Grandparent', es: 'Abuelo/a' } },
  { value: 'sibling', label: { en: 'Sibling', es: 'Hermano/a' } },
  { value: 'child', label: { en: 'Child', es: 'Hijo/a' } },
  { value: 'spouse', label: { en: 'Spouse/Partner', es: 'Esposo/a' } },
  { value: 'friend', label: { en: 'Friend', es: 'Amigo/a' } },
  { value: 'mentor', label: { en: 'Mentor', es: 'Mentor' } },
  { value: 'colleague', label: { en: 'Colleague', es: 'Colega' } },
  { value: 'other', label: { en: 'Other', es: 'Otro' } },
];

const frequencies = [
  { value: 'daily', label: { en: 'Daily', es: 'Diario' } },
  { value: 'weekly', label: { en: 'Weekly', es: 'Semanal' } },
  { value: 'biweekly', label: { en: 'Bi-weekly', es: 'Quincenal' } },
  { value: 'monthly', label: { en: 'Monthly', es: 'Mensual' } },
];

export function AddRecipientDialog({ open, onOpenChange }: AddRecipientDialogProps) {
  const { user } = useAuth();
  const { language } = useLanguage();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    recipientName: '',
    contactType: 'phone',
    contactValue: '',
    relationship: 'friend',
    frequency: 'weekly',
    notes: '',
  });

  const content = {
    en: {
      title: 'Add Recipient',
      description: 'Set up automatic AI-generated announcements for this person',
      name: 'Recipient Name',
      namePlaceholder: 'e.g., Mom, John, Grandma',
      contactType: 'Contact Method',
      contactValue: 'Contact Info',
      contactPlaceholder: 'Enter phone number, email, or username',
      relationship: 'Relationship',
      frequency: 'How Often',
      notes: 'Personal Notes (Optional)',
      notesPlaceholder: 'Add context to help AI write more personalized messages (e.g., shared memories, inside jokes, recent events)',
      cancel: 'Cancel',
      add: 'Add Recipient',
      adding: 'Adding...',
    },
    es: {
      title: 'Agregar Destinatario',
      description: 'Configura anuncios automáticos generados por IA para esta persona',
      name: 'Nombre del Destinatario',
      namePlaceholder: 'ej., Mamá, Juan, Abuela',
      contactType: 'Método de Contacto',
      contactValue: 'Info de Contacto',
      contactPlaceholder: 'Ingresa teléfono, correo o usuario',
      relationship: 'Relación',
      frequency: 'Con qué Frecuencia',
      notes: 'Notas Personales (Opcional)',
      notesPlaceholder: 'Agrega contexto para ayudar a la IA a escribir mensajes más personalizados (ej., recuerdos compartidos, bromas internas, eventos recientes)',
      cancel: 'Cancelar',
      add: 'Agregar Destinatario',
      adding: 'Agregando...',
    },
  };

  const t = content[language as keyof typeof content] || content.en;

  const mutation = useMutation({
    mutationFn: async () => {
      // Validate input data
      const validationResult = recipientSchema.safeParse(formData);
      if (!validationResult.success) {
        const errorMessage = validationResult.error.errors[0]?.message || 'Invalid input';
        throw new Error(errorMessage);
      }
      
      const validatedData = validationResult.data;
      const nextScheduled = new Date();
      nextScheduled.setHours(nextScheduled.getHours() + 24);

      const { error } = await supabase
        .from('auto_compose_recipients')
        .insert([{
          user_id: user!.id,
          recipient_name: validatedData.recipientName,
          contact_type: validatedData.contactType,
          contact_value: validatedData.contactValue,
          relationship: validatedData.relationship,
          delivery_frequency: validatedData.frequency,
          notes: validatedData.notes || null,
          next_scheduled_at: nextScheduled.toISOString(),
        }]);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auto-compose-recipients'] });
      toast.success(({ en: 'Recipient added!', es: '¡Destinatario agregado!', fr: 'Destinataire ajouté !', pt: 'Destinatário adicionado!', ar: 'تمت إضافة المستلم!', ja: '受信者を追加しました！', ko: '수신자 추가됨!', id: 'Penerima ditambahkan!' }[language]) || 'Recipient added!');
      onOpenChange(false);
      setFormData({
        recipientName: '',
        contactType: 'phone',
        contactValue: '',
        relationship: 'friend',
        frequency: 'weekly',
        notes: '',
      });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to add recipient');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-primary" />
            {t.title}
          </DialogTitle>
          <DialogDescription>{t.description}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="recipientName">{t.name}</Label>
            <Input
              id="recipientName"
              placeholder={t.namePlaceholder}
              value={formData.recipientName}
              onChange={(e) => setFormData({ ...formData, recipientName: e.target.value })}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t.contactType}</Label>
              <Select
                value={formData.contactType}
                onValueChange={(value) => setFormData({ ...formData, contactType: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {contactTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <span className="flex items-center gap-2">
                        <type.icon className="h-4 w-4" />
                        {type.label[language]}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="contactValue">{t.contactValue}</Label>
              <Input
                id="contactValue"
                placeholder={t.contactPlaceholder}
                value={formData.contactValue}
                onChange={(e) => setFormData({ ...formData, contactValue: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t.relationship}</Label>
              <Select
                value={formData.relationship}
                onValueChange={(value) => setFormData({ ...formData, relationship: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {relationships.map((rel) => (
                    <SelectItem key={rel.value} value={rel.value}>
                      {rel.label[language]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t.frequency}</Label>
              <Select
                value={formData.frequency}
                onValueChange={(value) => setFormData({ ...formData, frequency: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {frequencies.map((freq) => (
                    <SelectItem key={freq.value} value={freq.value}>
                      {freq.label[language]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">{t.notes}</Label>
            <Textarea
              id="notes"
              placeholder={t.notesPlaceholder}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="min-h-[80px]"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t.cancel}
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? t.adding : t.add}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
